import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/core/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import EventIcon from '@material-ui/icons/Event';
import MovieFilterIcon from '@material-ui/icons/MovieFilter';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import AssessmentIcon from '@material-ui/icons/Assessment';
import GroupIcon from '@material-ui/icons/Group';
import LaunchIcon from '@material-ui/icons/Launch';
import DescriptionIcon from '@material-ui/icons/Description';
import Container from 'common/Container';
import { registerBusinessLevelUser } from '../../../services/userService';

/** Marca del evento: TNOCON (lockup tipográfico TNO + CON sin espacio). */
const BrandTnoCon = ({ onDark = false, sxTno = {}, sxCon = {}, dense = false }) => (
  <Box component="span" sx={{ whiteSpace: 'nowrap', display: 'inline' }}>
    <Box
      component="span"
      sx={{
        fontWeight: dense ? 800 : 900,
        letterSpacing: dense ? '-0.05em' : '-0.07em',
        textShadow: dense
          ? 'none'
          : '0.02em 0 0 currentColor, -0.02em 0 0 currentColor, 0 0.02em 0 currentColor, 0 -0.02em 0 currentColor',
        ...sxTno,
      }}
    >
      TNO
    </Box>
    <Box
      component="span"
      sx={{
        fontWeight: dense ? 500 : 200,
        letterSpacing: dense ? '-0.05em' : '-0.07em',
        marginLeft: dense ? '-0.05em' : '-0.06em',
        color: onDark ? 'rgba(255,255,255,0.78)' : '#4a505c',
        ...sxCon,
      }}
    >
      CON
    </Box>
  </Box>
);

const portfolioItems = [
  {
    title: 'Grilla 2026',
    icon: EventIcon,
    description:
      'En TNOCON verá en primicia la programación que sostiene la señal TNO Network: más de 25 programas en vivo (tecnología, negocios, entretenimiento, bienestar) con audiencias por nicho.',
  },
  {
    title: 'Proyectos Media Lab',
    icon: MovieFilterIcon,
    description:
      'El brazo creativo de TNO Network: formatos originales y On-Demand que mostramos en TNOCON para que entienda cómo su marca vive dentro del ecosistema, no solo en un spot.',
  },
  {
    title: 'Ad-Tech & product placement',
    icon: TouchAppIcon,
    description:
      'Recorrido por cómo la señal integra marcas en +40 producciones On-Demand: visibilidad que acompaña al contenido, alineada con el tono TNO Network.',
  },
  {
    title: 'Data Hub',
    icon: AssessmentIcon,
    description:
      'Transparencia de alcance: +90.000 seguidores y +30.000 visitas mensuales en web — el respaldo detrás de la conversación que TNO Network construye cada día.',
  },
  {
    title: 'TNO Digest',
    icon: DescriptionIcon,
    description:
      'Editorial en tnonetwork.com: piezas con enfoque SEO y autoridad, en línea con la señal y con espacio para narrativas de marca cuando el plan lo contempla.',
  },
  {
    title: 'Networking VIP',
    icon: GroupIcon,
    description:
      'TNOCON es también el lugar de encuentro: tiempo cara a cara con dirección comercial para alinear narrativa de marca con la realidad de la parrilla y del Media Lab.',
  },
  {
    title: 'Nueva plataforma',
    icon: LaunchIcon,
    description:
      'Avance de la interfaz y herramientas de segmentación y pauta — y cómo conviven con la web, el streaming y el TNO Digest en un solo ecosistema alrededor de su marca.',
  },
];

const sectors = [
  'Retail',
  'Consumo masivo',
  'Tecnología',
  'Servicios',
  'Salud y bienestar',
  'Finanzas',
  'Entretenimiento',
  'Otro',
];

const EVENT_DATE = new Date('2026-04-22T10:00:00-04:00');
const EVENT_MAPS_URL =
  process.env.REACT_APP_BUSINESS_LEVEL_MAPS_URL ||
  process.env.REACT_APP_PRESSSTART_MAPS_URL ||
  'https://share.google/W53TGyHp3m8uSLD5y';

const getCountdownParts = (targetDate) => {
  const now = new Date();
  const diffMs = Math.max(targetDate.getTime() - now.getTime(), 0);
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
};

const scrollToForm = () => {
  const el = document.getElementById('business-level-form');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const LandingBusinessLevel = () => {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    representativeRole: '',
    corporateWhatsapp: '',
    companyBrand: '',
    sector: '',
    webSocials: '',
    interestSponsorship: false,
    interestMediaLab: false,
    interestWeb: false,
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });
  const [countdown, setCountdown] = useState(() => getCountdownParts(EVENT_DATE));

  useEffect(() => {
    const t = setInterval(() => setCountdown(getCountdownParts(EVENT_DATE)), 1000);
    return () => clearInterval(t);
  }, []);

  const handleChange = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: v }));
    if (status.type) setStatus({ type: null, message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const anyInterest =
      form.interestSponsorship || form.interestMediaLab || form.interestWeb;
    if (!anyInterest) {
      setStatus({
        type: 'error',
        message: 'Seleccione al menos un interés principal para continuar.',
      });
      return;
    }
    if (!String(form.sector || '').trim()) {
      setStatus({ type: 'error', message: 'Seleccione el sector de la empresa.' });
      return;
    }
    setLoading(true);
    setStatus({ type: null, message: '' });
    const result = await registerBusinessLevelUser(form);
    setLoading(false);
    if (!result.success) {
      setStatus({
        type: 'error',
        message: result.error || 'No se pudo enviar el registro. Intenta de nuevo.',
      });
      return;
    }
    setStatus({
      type: 'success',
      message:
        'Registro recibido para TNOCON · The Business Level. Revisa tu correo (y spam): acreditación y datos del encuentro. Mercadeo TNO Network dará seguimiento a su marca.',
    });
    setForm({
      fullName: '',
      email: '',
      representativeRole: '',
      corporateWhatsapp: '',
      companyBrand: '',
      sector: '',
      webSocials: '',
      interestSponsorship: false,
      interestMediaLab: false,
      interestWeb: false,
    });
  };

  return (
    <Box>
      <Box
        minHeight={{ xs: 'auto', md: 'calc(100vh - 64px)' }}
        position={'relative'}
        sx={{
          background:
            'linear-gradient(130deg, rgba(13,22,66,0.97) 0%, rgba(26,35,126,0.95) 45%, rgba(55,71,160,0.9) 78%, rgba(201,162,39,0.22) 100%)',
          color: 'common.white',
          overflow: 'hidden',
          borderBottom: '4px solid #c9a227',
          /* Mismo lenguaje que Press Start / Creators Guild: halo circular arriba a la derecha */
          '&:before': {
            content: '" "',
            position: 'absolute',
            top: -120,
            right: -120,
            width: 360,
            height: 360,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,214,120,0.5), rgba(201,162,39,0.28) 38%, rgba(201,162,39,0))',
            pointerEvents: 'none',
            zIndex: 0,
          },
          '&:after': {
            content: '" "',
            position: 'absolute',
            bottom: -100,
            left: -80,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(201,162,39,0.22), rgba(201,162,39,0))',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Container>
          <Box py={{ xs: 6, md: 10 }} position={'relative'} zIndex={2}>
            <Chip
              label={(
                <span>
                  <BrandTnoCon onDark />
                  <Box component="span" sx={{ fontWeight: 700 }}>{' '}2026 · THE BUSINESS LEVEL</Box>
                </span>
              )}
              sx={{ mb: 3, color: 'common.white', bgcolor: 'rgba(255,255,255,0.12)' }}
            />
            <Box
              width={{ xs: 320, md: 480 }}
              height={{ xs: 96, md: 140 }}
              sx={{
                mb: 3,
                backgroundImage: 'url(/images/logos/logo_white.PNG)',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                backgroundPosition: 'left center',
              }}
            />
            <Typography variant={'h2'} sx={{ fontWeight: 800, maxWidth: 920 }} gutterBottom>
              <BrandTnoCon onDark /> 2026: The Business Level
            </Typography>
            <Typography variant={'h5'} sx={{ maxWidth: 880, opacity: 0.95, fontWeight: 500 }} paragraph>
              La convención anual de TNO Network: un día para conocer la casa —señal multiplataforma, radio visual y Media Lab—
              y cerrar <BrandTnoCon onDark /> con quienes hacen posible que las marcas hablen con la misma audiencia que ya nos elige.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={scrollToForm}
              sx={{
                mt: 1,
                mb: 3,
                fontWeight: 800,
                backgroundColor: '#c9a227',
                color: '#1a237e',
                '&:hover': { backgroundColor: '#e8c547' },
              }}
            >
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                <Box component="span">Acreditación corporativa ·</Box>
                <BrandTnoCon dense sxTno={{ color: '#1a237e' }} sxCon={{ color: '#1a237e', opacity: 0.92 }} />
              </Box>
            </Button>
            <Typography variant={'body2'} sx={{ mb: 2, color: 'rgba(255,255,255,0.85)' }}>
              Cierre de <BrandTnoCon onDark dense /> · Miércoles 22 de abril de 2026 · 10:00 a. m. · Edif. Manzanares, Piso 2, Las Mercedes.
            </Typography>
            <Link href={EVENT_MAPS_URL} target="_blank" rel="noreferrer" sx={{ color: '#ffe082', fontWeight: 700 }}>
              Ver ubicación en Google Maps
            </Link>
            <Box mt={4}>
              <Card
                sx={{
                  p: { xs: 2, md: 3 },
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  maxWidth: 560,
                }}
              >
                <Typography variant={'subtitle1'} sx={{ fontWeight: 700, mb: 0.5, color: 'common.white' }}>
                  Faltan {countdown.days} días para <BrandTnoCon onDark dense /> 2026 — reveal de la Grilla TNO Network
                </Typography>
                <Typography variant={'caption'} display="block" sx={{ mb: 2, color: 'rgba(255,255,255,0.85)' }}>
                  Cuenta regresiva · <BrandTnoCon onDark dense /> · The Business Level
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    { label: 'Días', value: countdown.days },
                    { label: 'Horas', value: countdown.hours },
                    { label: 'Minutos', value: countdown.minutes },
                    { label: 'Segundos', value: countdown.seconds },
                  ].map((item) => (
                    <Grid item xs={3} key={item.label}>
                      <Box textAlign={'center'}>
                        <Typography variant={'h4'} sx={{ fontWeight: 900, color: 'common.white' }}>
                          {String(item.value).padStart(2, '0')}
                        </Typography>
                        <Typography variant={'caption'} sx={{ color: 'rgba(255,255,255,0.88)' }}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container>
        <Box py={6}>
          <Typography variant={'h3'} gutterBottom sx={{ fontWeight: 800 }}>
            Qué es <BrandTnoCon dense sxTno={{ color: 'text.primary' }} sxCon={{ color: 'text.secondary' }} />
          </Typography>
          <Typography variant={'body1'} color={'textSecondary'} sx={{ lineHeight: 1.95, fontSize: '1.06rem', mb: 3 }}>
            <BrandTnoCon dense sxTno={{ color: 'text.primary' }} sxCon={{ color: 'text.secondary' }} /> es la convención anual de{' '}
            <strong>TNO Network</strong>: el momento en el que abrimos la cocina —talentos, parrilla, tecnología y visión editorial—
            para quienes forman parte del ecosistema y para quienes quieren sumarse. No es solo una invitación a invertir: es ver
            cómo se produce la señal que hoy combina <strong>streaming, audio, video y comunidad</strong> bajo una misma marca.
          </Typography>
          <Typography variant={'body1'} color={'textSecondary'} sx={{ lineHeight: 1.95, fontSize: '1.06rem', mb: 4 }}>
            <strong>The Business Level</strong> es el cierre de <BrandTnoCon dense sxTno={{ color: 'text.primary' }} sxCon={{ color: 'text.secondary' }} />: el track pensado para{' '}
            <strong>gerentes de marca, mercadeo y aliados comerciales</strong>. Aquí la conversación pasa de la curiosidad a la
            hoja de ruta —siempre con el ADN TNO Network delante.
          </Typography>

          <Typography variant={'h3'} gutterBottom sx={{ fontWeight: 800 }}>
            TNO Network y el Media Lab: más que espacio, ecosistema
          </Typography>
          <Typography variant={'body1'} color={'textSecondary'} sx={{ lineHeight: 1.95, fontSize: '1.06rem', mb: 4 }}>
            Somos una red de contenido multiplataforma con raíz en la <strong>radio visual</strong> y ambición digital: programas en vivo,
            biblioteca On-Demand, presencia web, <strong>TNO Digest</strong> (editorial en tnonetwork.com) y narrativas que acompañan
            a la audiencia más allá del estudio. El <strong>Media Lab</strong> es donde esa identidad se traduce en formatos,
            integraciones de marca y experimentos que usted verá contextualizados en{' '}
            <BrandTnoCon dense sxTno={{ color: 'text.primary' }} sxCon={{ color: 'text.secondary' }} /> —no como catálogo abstracto, sino como experiencia.
          </Typography>

          <Typography variant={'h3'} gutterBottom sx={{ fontWeight: 800 }}>
            Lo que verá en <BrandTnoCon dense sxTno={{ color: 'text.primary' }} sxCon={{ color: 'text.secondary' }} /> · portafolio 2026
          </Typography>
          <Grid container spacing={3} sx={{ mb: 2 }}>
            {portfolioItems.map((item) => (
              <Grid item xs={12} md={6} key={item.title}>
                <Card variant={'outlined'} sx={{ p: 3, height: '100%', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                  <Box
                    width={44}
                    height={44}
                    borderRadius={2}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    mb={1.5}
                    sx={{ backgroundColor: 'primary.main', color: 'common.white' }}
                  >
                    <item.icon />
                  </Box>
                  <Typography variant={'h5'} gutterBottom sx={{ fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Typography color={'textSecondary'}>{item.description}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography variant={'h3'} gutterBottom sx={{ fontWeight: 800, mt: { xs: 5, sm: 6, md: 8 } }}>
            Los productos audiovisuales que componen nuestra señal
          </Typography>
          <Typography
            variant={'body1'}
            color={'textSecondary'}
            sx={{ mb: 3, maxWidth: 920, lineHeight: 1.8, fontSize: '1.05rem' }}
          >
            En <BrandTnoCon dense sxTno={{ color: 'text.primary' }} sxCon={{ color: 'text.secondary' }} /> le contamos{' '}
            <strong>cuáles son esos productos</strong>: parrilla en vivo, catálogo On-Demand, verticales con especialistas,{' '}
            <strong>TNO Digest</strong> y el stack (radio visual, web, redes, producción propia) —cómo se articulan y qué rol juega cada pieza en el ecosistema TNO Network.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              {
                kpi: 'Alcance',
                text: '+25 programas en vivo con transmisión global y +40 producciones On-Demand: la parrilla viva y la biblioteca a la carta que definen el ritmo de la conversación en TNO Network.',
              },
              {
                kpi: 'Verticales',
                text: 'Formatos y especialistas por tema: misma parrilla, distintas “líneas” de contenido con rigor y tono acorde a cada vertical.',
              },
              {
                kpi: 'Innovación',
                text: 'Referentes en radio visual en Venezuela, integrados con web, social, TNO Digest y producción propia —la base de cada edición de TNOCON.',
              },
            ].map((row) => (
              <Grid item xs={12} md={4} key={row.kpi}>
                <Card variant={'outlined'} sx={{ p: 2.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant={'h6'} color="primary" sx={{ fontWeight: 800 }}>
                    {row.kpi}
                  </Typography>
                  <Typography variant={'body2'} color="textSecondary" sx={{ mt: 1, lineHeight: 1.7 }}>
                    {row.text}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <Box sx={{ backgroundColor: 'alternate.main' }} id="business-level-form">
        <Container>
          <Box py={6}>
            <Typography variant={'h3'} gutterBottom sx={{ fontWeight: 800 }}>
              Coordenadas · cierre de <BrandTnoCon dense sxTno={{ color: 'text.primary' }} sxCon={{ color: 'text.secondary' }} />
            </Typography>
            <Typography paragraph>
              <strong>The Business Level</strong> —última jornada de <BrandTnoCon dense sxTno={{ color: 'text.primary' }} sxCon={{ color: 'text.secondary' }} /> 2026.
              <br />
              <br />
              Fecha: Miércoles, 22 de abril de 2026.
              <br />
              Hora: 10:00 a. m.
              <br />
              Lugar: Final Av. Principal de Las Mercedes, Edif. Manzanares, Piso 2 (lateral al C.C. Paseo Las Mercedes).
            </Typography>
            <Typography paragraph color={'textSecondary'}>
              Estacionamiento: Centro Comercial Paseo Las Mercedes (vehículo propio).
            </Typography>
            <Button variant="contained" color="primary" href={EVENT_MAPS_URL} target="_blank" rel="noreferrer" sx={{ mb: 3 }}>
              Abrir en Google Maps
            </Button>
          </Box>
        </Container>
      </Box>

      <Container>
        <Box py={6}>
          <Typography variant={'h3'} align={'center'} sx={{ fontWeight: 800 }} gutterBottom>
            Reserve su lugar en <BrandTnoCon dense sxTno={{ color: 'text.primary' }} sxCon={{ color: 'text.secondary' }} />
          </Typography>
          <Typography align={'center'} color={'textSecondary'} paragraph>
            Formulario de acreditación para The Business Level: mercadeo, marca y negocios dentro de la convención TNO Network.
            Tras enviar, recibirá confirmación y detalle del encuentro por correo.
          </Typography>

          <Box component={'form'} maxWidth={800} mx={'auto'} onSubmit={handleSubmit}>
            <Card variant={'outlined'} sx={{ p: { xs: 2, md: 4 } }}>
              {status.type === 'success' && (
                <Alert severity={'success'} sx={{ mb: 2 }} onClose={() => setStatus({ type: null, message: '' })}>
                  {status.message}
                </Alert>
              )}
              {status.type === 'error' && (
                <Alert severity={'error'} sx={{ mb: 2 }} onClose={() => setStatus({ type: null, message: '' })}>
                  {status.message}
                </Alert>
              )}

              <Typography variant={'h6'} sx={{ fontWeight: 700 }} gutterBottom>
                Datos del representante
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField label={'Nombre y apellido'} required fullWidth value={form.fullName} onChange={handleChange('fullName')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label={'Cargo'} required fullWidth value={form.representativeRole} onChange={handleChange('representativeRole')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Correo corporativo'}
                    type={'email'}
                    required
                    fullWidth
                    value={form.email}
                    onChange={handleChange('email')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'WhatsApp corporativo'}
                    required
                    fullWidth
                    value={form.corporateWhatsapp}
                    onChange={handleChange('corporateWhatsapp')}
                    placeholder="+58 ..."
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant={'h6'} sx={{ fontWeight: 700 }} gutterBottom>
                Datos de la empresa
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Razón social / marca'}
                    required
                    fullWidth
                    value={form.companyBrand}
                    onChange={handleChange('companyBrand')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField select label={'Sector'} required fullWidth value={form.sector} onChange={handleChange('sector')}>
                    <MenuItem value="">
                      <em>Seleccione</em>
                    </MenuItem>
                    {sectors.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={'Página web / redes'}
                    fullWidth
                    value={form.webSocials}
                    onChange={handleChange('webSocials')}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant={'h6'} sx={{ fontWeight: 700 }} gutterBottom>
                Interés principal
              </Typography>
              <Box display={'flex'} flexDirection={'column'}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.interestSponsorship}
                      onChange={handleChange('interestSponsorship')}
                      color="primary"
                    />
                  )}
                  label="Patrocinio de programas en vivo"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.interestMediaLab}
                      onChange={handleChange('interestMediaLab')}
                      color="primary"
                    />
                  )}
                  label="Producción de contenido original (Media Lab)"
                />
                <FormControlLabel
                  control={(
                    <Checkbox checked={form.interestWeb} onChange={handleChange('interestWeb')} color="primary" />
                  )}
                  label="Presencia web, TNO Digest y editorial"
                />
              </Box>

              <Box sx={{ mt: 4 }}>
                <Button
                  variant={'contained'}
                  color="primary"
                  size={'large'}
                  type={'submit'}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color={'inherit'} /> : null}
                  sx={{ fontWeight: 800 }}
                >
                  {loading ? (
                    'Enviando...'
                  ) : (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Box component="span">Confirmar asistencia ·</Box>
                      <BrandTnoCon dense sxTno={{ color: 'inherit' }} sxCon={{ color: 'inherit', opacity: 0.95 }} />
                      <Box component="span">Business</Box>
                    </Box>
                  )}
                </Button>
              </Box>
            </Card>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingBusinessLevel;
